import React, { forwardRef, useCallback, useEffect, useState, useRef, useImperativeHandle } from 'react';
import { useDropzone } from 'react-dropzone';
import ReactCrop from 'react-image-crop';
import { Button, Modal } from 'react-bootstrap';
import 'react-image-crop/dist/ReactCrop.css';
import { useTranslation } from 'react-i18next';

const UploadImage = forwardRef((props, ref) => {
  const [show, setShow] = useState(false);
  const { t } = useTranslation('common');

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  // Crop Img Code Start
  const imgRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const [crop, setCrop] = useState({ unit: '%', width: 30, aspect: 4 / 3 });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [validatedSelectedFile, setValidatedSelectedFile] = useState({});

  const uploadCroppedImg = (canvas, crop) => {
    if (!crop || !canvas) {
      return;
    }
    const canvasRef = previewCanvasRef.current;
    const imageData64 = canvasRef.toDataURL('image/png');
    const fileUrl = base64StringtoFile(imageData64, 'test');
    return URL.createObjectURL(fileUrl);
  };

  useEffect(() => {
    if (!completedCrop || !previewCanvasRef.current || !imgRef.current) {
      return;
    }

    const image = imgRef.current;
    const canvas = previewCanvasRef.current;
    const crop = completedCrop;
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext('2d');
    const pixelRatio = window.devicePixelRatio;

    canvas.width = crop.width * pixelRatio;
    canvas.height = crop.height * pixelRatio;

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(image, crop.x * scaleX, crop.y * scaleY, crop.width * scaleX, crop.height * scaleY, 0, 0, crop.width, crop.height);
  }, [completedCrop]);

  const [files, setFiles] = useState([]);
  const [loader, setLoader] = useState(false);
  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
    accept: 'image/*',
    onDrop: (acceptedFiles) => {
      const fileSize = (acceptedFiles[0].size / 1024 / 1024).toFixed(0);
      if (fileSize > 5) {
        setValidatedSelectedFile({
          sizeError: t('UPLOAD_IMAGE.SIZE_ERROR'),
        });

        return false;
      }
      setLoader(true);
      setValidatedSelectedFile({});
      setFiles(
        acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          }),
        ),
      );
    },
  });

  const onLoad = useCallback((img) => {
    imgRef.current = img;
    setLoader(false);
  }, []);

  const thumbs = files.map((file) => (
    <ReactCrop
      key={file.name}
      style={{ verticalAlign: 'bottom' }}
      src={file.preview}
      onImageLoaded={onLoad}
      crop={crop}
      onChange={(c) => setCrop(c)}
      onComplete={(c) => setCompletedCrop(c)}
    />
  ));

  function base64StringtoFile(base64String, filename) {
    const arr = base64String.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  }

  const preview = files.map((file, index) => (
    <canvas
      key={file?.name || index} // Ensure a unique key
      ref={previewCanvasRef}
      style={{
        width: Math.round(completedCrop?.width ?? 0),
        height: Math.round(completedCrop?.height ?? 0),
      }}
    />
  ));

  useEffect(
    () => () => {
      files.forEach((file) => URL.revokeObjectURL(file.preview));
    },
    [files],
  );

  useImperativeHandle(ref, () => ({
    async showCroppedImg() {
      return uploadCroppedImg(previewCanvasRef.current, completedCrop);
    },
  }));

  return (
    <>
      <Button variant="primary" onClick={handleShow}>
        {t('UPLOAD_IMAGE.UPLOAD_IMG')}
      </Button>

      <Modal className="custom-modal modal-large" show={show} onHide={handleClose}>
        <Modal.Header>
          <Modal.Title>{t('UPLOAD_IMAGE.MODAL_TITLE')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="bom-image-upload">
            <div className="upload-image-section p-4">
              <div className="drag-drop-section">
                <div className="drop-box">
                  <div className={isDragActive ? 'logo_add border-toted' : 'logo_add border-solid'}>
                    <div className="container">
                      <div
                        {...getRootProps({
                          isDragActive,
                          isDragAccept,
                          isDragReject,
                        })}
                      >
                        <input {...getInputProps()} />
                        {isDragActive ? (
                          <p className="mb-0">{t('UPLOAD_IMAGE.DROP_FILES_HERE')}</p>
                        ) : (
                          <p className="mb-0">{t('UPLOAD_IMAGE.DRAG_DROP_MESSAGE')}</p>
                        )}
                      </div>
                    </div>

                    <label className="upload-icon ">
                      <input {...getInputProps()} type="file" accept="image/*" className="img-Upload" />
                      <a href={() => false} className="align-items-center justify-content-center">
                        <span className="icon-upload" />
                      </a>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {
              // show file error Massage
              Object.keys(validatedSelectedFile).length !== 0 && (
                <div className="d-flex justify-content-center align-items-center">
                  <span className="text-danger">{validatedSelectedFile.sizeError}</span>
                </div>
              )
            }

            <div className="upload-crop-section mt-3">
              <div classNames="crop-box">
                <label className="title">{t('UPLOAD_IMAGE.IMAGE_PREVIEW_TITLE')}</label>
                <div className="image-view-box">
                  {loader && (
                    <div className="w-100 bg-dark text-center">
                      <span className="text-light">{t('UPLOAD_IMAGE.LOADING_MESSAGE')}</span>
                    </div>
                  )}
                  <div className="d-flex justify-content-center">
                    <div className="col-md-5 p-0">{thumbs}</div>
                  </div>
                  <div style={{ display: 'none' }}>{preview}</div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-buttons mt-3 d-flex justify-content-between">
            <button className="btn btn-default">{t('UPLOAD_IMAGE.NO')}</button>
            <button className="btn btn-default">{t('UPLOAD_IMAGE.YES')}</button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
});

export default UploadImage;
